import { usePortal } from "@gorhom/portal";
import {
  CheckCircle,
  RotateCcw,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from "@tamagui/lucide-icons";
import React from "react";
import type { CropperProps } from "react-easy-crop";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { useWindowDimensions, View, XStack, YStack } from "tamagui";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { getCroppedImg } from "./utils";

export const CropImage = ({
  image,
  onCropDone,
  onClose,
  aspect,
  showGrid,
  cropShape,
}: {
  image: string;
  onCropDone: (f: Blob | null) => void;
  onClose: () => void;
} & Partial<CropperProps>) => {
  const { updatePortal } = usePortal("bottomContent");
  const { height } = useWindowDimensions();

  const [crop, setCrop] = React.useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(
    null,
  );

  const onCropComplete = React.useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const onPreviewVerified = React.useCallback(async () => {
    if (!croppedAreaPixels) return;

    try {
      // Get the cropped image as a File
      const croppedFile = await getCroppedImg(
        image,
        croppedAreaPixels,
        rotation,
      );

      // Perform the upload or any further processing with croppedFile
      onCropDone(croppedFile);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  }, [onCropDone, rotation, croppedAreaPixels, image]);

  React.useEffect(() => {
    updatePortal(
      "crop",
      <View
        key={"crop"}
        width="100%"
        backgroundColor={"$muted"}
        height={height}
      >
        <View flex={1}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            aspect={aspect}
            showGrid={showGrid}
            cropShape={cropShape}
          />
          )
        </View>

        <View>
          <YStack
            gap={"$medium"}
            flex={1}
            backgroundColor={"$backgroundBlock"}
            padding={"$space.large"}
          >
            <XStack
              gap={"$medium"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Body>Edit until you are ready to upload. ✨</Body>
            </XStack>
            <XStack gap={"$medium"} justifyContent={"space-between"}>
              <Button
                type={"secondary"}
                icon={ZoomIn}
                onPress={() => {
                  setZoom((currentZoom) => Math.min(3, currentZoom + 0.3));
                }}
              />
              <Button
                type={"secondary"}
                icon={ZoomOut}
                onPress={() => {
                  setZoom((currentZoom) => Math.max(1, currentZoom - 0.3));
                }}
              />
              <Button
                type={"secondary"}
                icon={RotateCw}
                onPress={() => {
                  setRotation(
                    (currentRotation) =>
                      Math.ceil(currentRotation / 45) * 45 + 45,
                  );
                }}
              />
              <Button
                type={"secondary"}
                icon={RotateCcw}
                onPress={() => {
                  setRotation(
                    (currentRotation) =>
                      Math.ceil(currentRotation / 45) * 45 - 45,
                  );
                }}
              />
            </XStack>
            <Button onPress={onPreviewVerified} icon={CheckCircle}>
              I'm ready, upload!
            </Button>
          </YStack>
          )
        </View>

        <Button
          type={"ghost"}
          icon={X}
          onPress={onClose}
          position={"absolute"}
          top={0}
          right={0}
        />
      </View>,
    );

    return () => updatePortal("crop", null);
  }, [
    image,
    aspect,
    showGrid,
    cropShape,
    crop,
    rotation,
    onPreviewVerified,
    height,
    zoom,
    onCropComplete,
    onClose,
    updatePortal,
  ]);

  return null;
};
